import { useState } from "react";
import { useParams } from "react-router-dom";

import {
  labeebTierPath,
  type LabeebPatchTierResponse,
  type LabeebTierRow,
} from "../../api/labeeb";
import { RecentChanges } from "../../components/audit/RecentChanges";
import { Gate } from "../../components/authority/Gate";
import { Breadcrumbs } from "../../components/Layout";
import { ResourceView } from "../../components/states";
import { Badge, Card, Notice, Page, Table, Td, Th } from "../../components/ui";
import { ConfirmDialog } from "../../components/write/ConfirmDialog";
import { WriteResult } from "../../components/write/EffectTiming";
import { WriteFailure } from "../../components/write/WriteFailure";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { useWrite } from "../../hooks/useWrite";
import { ProductTag } from "../../products/PerProduct";
import { editableAttributesOf, LABEEB_LIMIT_KEYS, limitLabel, limitValue } from "../../api/labeebLimits";

/**
 * One Labeeb tier's envelope, on its own page.
 *
 * The mirror of `/policy/caps/{tier}` for the other product. Labeeb's limits
 * used to be edited by an expander inside a table cell on the index, which
 * meant the fields, the code-default warning and the result all had to fit
 * in a column — so they were cramped where MCP's got a
 * page. Same data, same write, a surface the size of what it is doing.
 *
 * READS THE TIER BY ID. `GET /admin/tiers/{tier_id}` exists now; this page
 * used to fetch the whole list and select its row, because it did not, and
 * both this docstring and `nav/routes.ts` said so. One tier, one read — and a
 * 404 is now a real "no such tier" from the product rather than a row missing
 * from a list.
 */
export function LabeebTier({ config }: { config: AppConfig }) {
  const { tier = "" } = useParams();
  const tiers = useResource<LabeebTierRow>(config, labeebTierPath(tier), [tier]);
  const s = tiers.state;

  return (
    <Page
      title={<span className="ref">{tier}</span>}
      subtitle="What this tier grants a merchant in Labeeb."
    >
      <Breadcrumbs
        trail={[
          { label: "Overview", to: "/" },
          { label: "Tiers & limits", to: "/policy/tiers" },
          { label: tier },
        ]}
      />

      {/* A 404 here is the PRODUCT saying there is no such tier, not the
          gateway saying the route is unmounted — the by-id read exists. So it
          reads as a wrong id rather than a broken console. */}
      <ResourceView
        state={s}
        what="this tier"
        onRetry={tiers.reload}
        notFound={
          <div className="mt-4">
            <Notice title="No such tier">
              <p>
                <span className="ref">{tier}</span> is not a tier Labeeb knows. Check the id
                on{" "}
                <a className="underline" href="/policy/tiers">
                  Tiers &amp; limits
                </a>
                .
              </p>
            </Notice>
          </div>
        }
      />

      {s.kind === "ready" &&
        (() => {
          const row = s.data;

          // Not `EmptyByDesign`: an unknown tier id is a wrong URL, not an
          // empty collection, and telling an operator "nothing here" about a
          // typo sends them looking for a deleted tier. A 404 from the by-id
          // route is handled above and says the same thing.
          if (!row?.tier_id) {
            return (
              <div className="mt-4">
                <Notice title="No such tier">
                  <p>
                    <span className="ref">{tier}</span> is not among the tiers Labeeb
                    returns. Check the id on{" "}
                    <a className="underline" href="/policy/tiers">
                      Tiers &amp; limits
                    </a>
                    .
                  </p>
                </Notice>
              </div>
            );
          }

          const cols = [...LABEEB_LIMIT_KEYS];
          const attributes = editableAttributesOf(null, cols);

          return (
            <div className="mt-4 flex flex-col gap-6">
              <Card
                title={row.tier_id}
                aside={<ProductTag id="labeeb" />}
                note="Messages, tokens and Snowflake queries this tier grants"
              >
                {/* `source: "code_default"` is gone: a tier now either exists
                    or the by-id route 404s, so there is no "saving creates it"
                    case left to warn about. */}
                {row.is_default && (
                  <div className="mb-3">
                    <Badge>default tier</Badge>
                    <p className="mt-1.5 text-xs/5 text-slate-500 dark:text-slate-400">
                      Businesses with no tier of their own land here, so a change to
                      this envelope moves everyone who has never been assigned one.
                    </p>
                  </div>
                )}

                {cols.length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    This tier carries no stored limits.
                  </p>
                ) : (
                  <Table
                    head={
                      <tr>
                        <Th>Limit</Th>
                        <Th right>Value</Th>
                      </tr>
                    }
                  >
                    {cols.map((c) => (
                      <tr key={c}>
                        <Td>
                          <span className="ref">{c}</span>
                          <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                            {limitLabel(c)}
                          </span>
                        </Td>
                        <Td right>
                          <span className="tabular-nums">{limitValue(row, c)}</span>
                        </Td>
                      </tr>
                    ))}
                  </Table>
                )}
              </Card>

              <Card title="Change these limits" aside={<ProductTag id="labeeb" />}>
                <LabeebTierEditor
                  config={config}
                  row={row}
                  attributes={attributes}
                  onWritten={tiers.reload}
                />
              </Card>
            </div>
          );
        })()}

      <div className="mt-4">
        <RecentChanges
          config={config}
          product="labeeb"
          domain="quota"
          target={`tier/${tier}`}
        />
      </div>
    </Page>
  );
}

/**
 * The tier write, given a page to happen on.
 *
 * Every field is always visible here — no expander. The expander existed
 * because this lived in a table cell; on its own page hiding the form behind a
 * click only adds a step.
 */
function LabeebTierEditor({
  config,
  row,
  attributes,
  onWritten,
}: {
  config: AppConfig;
  row: LabeebTierRow;
  attributes: string[];
  onWritten: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const write = useWrite<LabeebPatchTierResponse>(config);
  const busy = write.state.kind === "submitting";

  // Only what was actually typed. The API applies exactly what the body names,
  // so sending untouched fields would rewrite values nobody chose to change.
  const changed = Object.entries(draft).filter(([, v]) => v.trim() !== "");

  function submit(reason: string) {
    const body: Record<string, number | string> = {};
    for (const [k, v] of changed) body[k] = Number(v);
    // EVERY CHANGE ON THIS API NEEDS BOTH, and the editor collected the
    // reason and threw it away. Without `expected_version` the write is a
    // 400, not a silent overwrite — the server refuses rather than guessing
    // which version the operator was looking at.
    body.expected_version = row.version ?? 0;
    body.reason = reason;
    void write
      .submit(labeebTierPath(row.tier_id), { method: "PATCH", body: JSON.stringify(body) })
      .then((r: LabeebPatchTierResponse | undefined) => {
        if (r) {
          onWritten();
          setDraft({});
        }
      });
    setConfirming(false);
  }

  return (
    <>
      <Gate need="quota:write">
        <div className="flex flex-col gap-3">
          {attributes.map((a) => (
            <label key={a} className="flex items-center justify-between gap-3 text-sm">
              <span>
                {limitLabel(a)}{" "}
                <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">
                  now {limitValue(row, a)}
                </span>
              </span>
              <input
                className="w-36 rounded border border-slate-950/10 dark:border-white/10 bg-white dark:bg-slate-900 px-2 py-1 text-sm tabular-nums"
                value={draft[a] ?? ""}
                disabled={busy}
                placeholder={limitValue(row, a)}
                onChange={(e) => setDraft({ ...draft, [a]: e.target.value })}
              />
            </label>
          ))}

          <p className="text-xs text-slate-500 dark:text-slate-400">
            &minus;1 means unlimited. A field left blank is left unchanged.
          </p>

          <div>
            <Button onClick={() => setConfirming(true)} disabled={busy || changed.length === 0}>
              Save
            </Button>
          </div>
        </div>
      </Gate>

      {write.state.kind === "done" && (
        <WriteResult title={`${row.tier_id} updated`} onDismiss={write.reset} />
      )}
      <WriteFailure
        state={write.state}
        onDismiss={write.reset}
        onReread={() => {
          write.reset();
          onWritten();
        }}
      />

      <ConfirmDialog
        open={confirming}
        title={`Change ${row.tier_id}`}
        confirmLabel="Save"
        requireReason
        reasonHint="e.g. DA-1234, raising the standard envelope"
        busy={busy}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
      >
        <p className="text-sm">
          {changed.map(([k, v]) => `${limitLabel(k)} → ${v}`).join(", ")}
        </p>
        {row.is_default && (
          <p className="mt-2 text-sm">
            This is the default tier, so this also changes every business that has
            never been assigned one.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}
